import type { UIMessage } from "ai";
import {
  getBrandAssistantOnboardingCompletionsFromParts,
  type BrandAssistantOnboardingCompletionData,
} from "@runcheetah/shared";

export type AssistantOnboardingCompletionReference = BrandAssistantOnboardingCompletionData;

type AssistantOnboardingCompletionMessage = Pick<UIMessage, "parts"> &
  Partial<Pick<UIMessage, "id" | "role">>;

const hiddenCompletionKinds: ReadonlySet<AssistantOnboardingCompletionReference["kind"]> = new Set([
  "first_asset_pending",
  "background_complete",
  "strategy_ready",
  "profile_updates_pending",
  "profile_updates_resolved",
  "visual_identity_ready",
]);

const liveProgressCompletionKinds: ReadonlySet<AssistantOnboardingCompletionReference["kind"]> =
  new Set(["brand_scan_in_progress", "strategy_generation_in_progress"]);

export function getAssistantOnboardingCompletionReferences(
  message: AssistantOnboardingCompletionMessage,
  options?: {
    messages?: readonly Pick<UIMessage, "id" | "parts" | "role">[];
    showProgress?: boolean;
    visibleAfterMessageIndex?: number | null;
  },
): AssistantOnboardingCompletionReference[] {
  const messages = options?.messages;
  const messageIndex = getMessageIndex(messages, message.id);

  const currentCompletions = getBrandAssistantOnboardingCompletionsFromParts(message.parts);
  const finalCompletion = getLatestCompletionOfKind(currentCompletions, "first_asset_cta");
  const visibleCompletions = currentCompletions.filter(
    (completion) =>
      !hiddenCompletionKinds.has(completion.kind) &&
      !liveProgressCompletionKinds.has(completion.kind) &&
      completion.kind !== "first_asset_cta",
  );

  if (finalCompletion) {
    if (messages && message.id && isFirstAssetCtaStale(messages, message.id)) {
      return visibleCompletions;
    }

    return [...visibleCompletions, normalizeFinalCompletion(finalCompletion)];
  }

  const progressCompletion = currentCompletions
    .toReversed()
    .find((completion) => liveProgressCompletionKinds.has(completion.kind));

  if (progressCompletion) {
    if (
      options?.showProgress === false ||
      isBeforeVisibleCompletionGate(messageIndex, options?.visibleAfterMessageIndex) ||
      (messages &&
        message.id &&
        getLatestLiveProgressCompletionMessageId(messages, options?.visibleAfterMessageIndex) !==
          message.id)
    ) {
      return visibleCompletions;
    }

    return [...visibleCompletions, progressCompletion];
  }

  return visibleCompletions;
}

function getLatestLiveProgressCompletionMessageId(
  messages: readonly Pick<UIMessage, "id" | "parts" | "role">[],
  visibleAfterMessageIndex?: number | null,
): string | null {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    if (isBeforeVisibleCompletionGate(messageIndex, visibleAfterMessageIndex)) {
      continue;
    }

    const message = messages[messageIndex];
    if (!message) {
      continue;
    }

    const completions = getBrandAssistantOnboardingCompletionsFromParts(message.parts);
    if (completions.some((completion) => completion.kind === "first_asset_cta")) {
      return null;
    }

    if (completions.some((completion) => liveProgressCompletionKinds.has(completion.kind))) {
      return message.id;
    }
  }

  return null;
}

function getMessageIndex(
  messages: readonly Pick<UIMessage, "id" | "parts">[] | undefined,
  messageId: string | undefined,
) {
  if (!messages || !messageId) {
    return null;
  }

  const index = messages.findIndex((candidate) => candidate.id === messageId);
  return index >= 0 ? index : null;
}

function isBeforeVisibleCompletionGate(
  messageIndex: number | null,
  visibleAfterMessageIndex: number | null | undefined,
) {
  return (
    messageIndex !== null &&
    visibleAfterMessageIndex !== null &&
    visibleAfterMessageIndex !== undefined &&
    messageIndex <= visibleAfterMessageIndex
  );
}

function getLatestCompletionOfKind(
  completions: readonly AssistantOnboardingCompletionReference[],
  kind: AssistantOnboardingCompletionReference["kind"],
) {
  return completions.toReversed().find((completion) => completion.kind === kind) ?? null;
}

function normalizeFinalCompletion(
  completion: AssistantOnboardingCompletionReference,
): AssistantOnboardingCompletionReference {
  return {
    ...completion,
    title: "Your brand is ready.",
    summary: "Your brand context is ready for the first asset.",
    ctaLabel: "Generate my first asset",
    ctaPrompt: "Generate my first asset.",
    visualIdentity: undefined,
  };
}

function isFirstAssetCtaStale(
  messages: readonly Pick<UIMessage, "id" | "parts" | "role">[],
  messageId: string,
) {
  const messageIndex = getMessageIndex(messages, messageId);
  if (messageIndex === null) {
    return false;
  }

  return messages.slice(messageIndex + 1).some((message) => {
    return message.parts.some((part) => {
      const type = (part as { type?: unknown }).type;
      return (
        type === "tool-preview_standalone_asset_batch_creation" ||
        type === "tool-generate_branded_image" ||
        type === "tool-edit_branded_image"
      );
    });
  });
}
